import { ModelAutomaticMutationSideEffectConfigs, SideEffectConfig, SideEffectConfigContext } from "./types";
import { CommonMutationMethods, CommonQueryMethods, ModelName, ParamsForMethod, ReturnForMethod } from "../../types";
import { extractRequestFromQueryKey } from "../../keys";
import { modelMatchesGetSomeRequest } from "./filter-utils";
import {
  prependToList,
  removeItemFromInfiniteDataList,
  updateInList,
  upsertInInfiniteDataList,
  removeFromList,
  getSortComparator,
  updateModelWithOptions,
  FORCE_INVALIDATE,
} from "../../utils";
import { TellescopeModelFromName } from "../helper-types";

type ExampleModel = "sms_messages";

type CommonExampleConfig = Partial<{
  [Method in CommonMutationMethods]: (
    request: ParamsForMethod<ExampleModel, Method>,
    response?: ReturnForMethod<ExampleModel, Method>,
    context?: SideEffectConfigContext
  ) => Pick<SideEffectConfig<ExampleModel>, CommonQueryMethods>;
}>;

/**
 * Generates standardized mutation side effect configurations for common CRUD operations.
 *
 * This factory function creates cache update logic for the four common mutation methods
 * (createOne, createSome, updateOne, deleteOne) and their effects on the three common
 * query methods (getSome, getByIds, getOne).
 *
 * The generated configs handle:
 * - Optimistic updates when response is undefined
 * - Filter-based cache inclusion/exclusion using modelMatchesGetSomeRequest
 * - Proper list manipulation (prepend, update, remove) using utility functions
 * - Consistent behavior across all models
 *
 * @template Model - The model name from the Tellescope API
 * @returns Standard mutation side effect configurations for the model
 *
 * @example
 * ```typescript
 * // Use as the base for a model's mutation configs
 * export const MY_MODEL_MUTATION_CONFIGS = {
 *   ...getCommonMutationSideEffectConfigs<"my_model">(),
 *   // Add model-specific overrides here
 * };
 * ```
 */
export const getCommonMutationSideEffectConfigs = <
  Model extends ModelName,
>(): ModelAutomaticMutationSideEffectConfigs<Model> => {
  // NOTE: the way we get around the crazy union types is to defined the common mutation side effects for one particular
  // "example model" and the do a type assertion for the requested type, since each model should be the exact same for
  // all common mutation/query pairs.
  const exampleConfig: CommonExampleConfig = {
    createOne: (_request, response) =>
      response
        ? {
            getOne: {
              exact: true,
              request: response.id,
              updater: () => null,
            },
            getSome: {
              exact: false,
              predicate: ({ queryKey }) => {
                const queryRequest = extractRequestFromQueryKey(queryKey);
                return modelMatchesGetSomeRequest(response, queryRequest);
              },
              updater: (oldData, context) => {
                const queryRequest = context?.queryKey ? extractRequestFromQueryKey(context.queryKey) : undefined;
                if (queryRequest?.search?.query) return FORCE_INVALIDATE;
                return upsertInInfiniteDataList({
                  infiniteData: oldData ?? { pages: [], pageParams: [] },
                  updater: response,
                  matchFn: (item) => item.id === response.id,
                  sortFn: getSortComparator({ sort: queryRequest?.sort, sortBy: queryRequest?.sortBy }),
                  limit: queryRequest?.limit,
                });
              },
            },
            getByIds: {
              exact: false,
              predicate: ({ queryKey }) => {
                const queryRequest = extractRequestFromQueryKey(queryKey);
                return !!queryRequest?.ids?.includes(response.id);
              },
              updater: (oldData) => ({
                ...oldData,
                matches: prependToList({ list: oldData?.matches ?? [], item: response }),
              }),
            },
          }
        : {},
    createSome: (_request, response) =>
      response
        ? {
            getOne: {
              predicate: ({ queryKey }) => {
                const request = extractRequestFromQueryKey(queryKey);
                const createdIds = response.created.map(({ id }) => id);
                if (typeof request === "string") {
                  return createdIds.includes(request);
                } else if (typeof request?.id === "string") {
                  return createdIds.includes(request.id);
                }

                return false; // TODO: add support for complex getOne requests
              },
              updater: () => null,
            },
            getSome: {
              exact: false,
              predicate: ({ queryKey }) => {
                const queryRequest = extractRequestFromQueryKey(queryKey);
                return response.created.some((item) => modelMatchesGetSomeRequest(item, queryRequest));
              },
              updater: (oldData, context) => {
                if (!context?.queryKey) return oldData;
                const queryRequest = extractRequestFromQueryKey(context.queryKey);
                const itemsToAdd = response.created.filter((item) => modelMatchesGetSomeRequest(item, queryRequest));
                const sortFn = getSortComparator({ sort: queryRequest?.sort, sortBy: queryRequest?.sortBy });
                return itemsToAdd.reduce(
                  (acc, curr) =>
                    upsertInInfiniteDataList({
                      infiniteData: acc,
                      updater: curr,
                      matchFn: (item) => item.id === curr.id,
                      sortFn,
                      limit: queryRequest?.limit,
                    }) ?? acc,
                  oldData ?? { pages: [], pageParams: [] }
                );
              },
            },
            getByIds: {
              exact: false,
              predicate: ({ queryKey }) => {
                const queryRequest = extractRequestFromQueryKey(queryKey);
                return !!queryRequest?.ids?.some((id) => response.created.some((item) => item.id === id));
              },
              updater: (oldData, context) => {
                if (!context?.queryKey) return oldData;
                const queryRequest = extractRequestFromQueryKey(context.queryKey);
                const itemsToAdd = response.created.filter((item) => queryRequest?.ids?.includes(item.id));
                return {
                  ...oldData,
                  matches: [...itemsToAdd, ...(oldData?.matches ?? [])],
                };
              },
            },
          }
        : {},
    updateOne: ({ _options, ...request }, response) => ({
      getOne: {
        request: request.id,
        exact: false,
        updater: (oldData) => {
          if (!oldData) return oldData;
          if (response) return response;
          return updateModelWithOptions(oldData, request, _options);
        },
      },
      getSome: {
        predicate: ({ state: { data }, queryKey }) => {
          if (!data) return false;
          if (data?.pages.some((page) => page.some((item) => item.id === request.id))) {
            return true;
          }
          if (!response) return false;

          const queryRequest = extractRequestFromQueryKey(queryKey);
          if (!queryRequest) return false;
          return modelMatchesGetSomeRequest(response, queryRequest);
        },
        updater: (oldData, context) => {
          const queryKey = context?.queryKey;
          if (!oldData || !queryKey) return oldData;
          const existingItem = oldData.pages.reduce<TellescopeModelFromName<ExampleModel> | undefined>((acc, page) => {
            if (acc) return acc;
            return page.find((item) => item.id === request.id);
          }, undefined);

          if (!existingItem && !response) {
            return oldData;
          }

          const newItem =
            response ?? (existingItem ? updateModelWithOptions(existingItem, request, _options) : undefined);
          if (!newItem) return oldData;

          const queryRequest = extractRequestFromQueryKey(queryKey);

          // Check if the updated item should remain in this query's results
          const shouldKeepInList = modelMatchesGetSomeRequest(newItem, queryRequest);

          if (shouldKeepInList) {
            return upsertInInfiniteDataList({
              infiniteData: oldData,
              updater: newItem,
              matchFn: (item) => item.id === request.id,
              sortFn: getSortComparator({ sort: queryRequest?.sort, sortBy: queryRequest?.sortBy }),
              limit: queryRequest?.limit,
            });
          }

          // Item no longer matches the filter, remove it from the list
          return {
            ...oldData,
            pages: oldData.pages.map((page) => page.filter((item) => item.id !== request.id)),
          };
        },
      },
      getByIds: {
        predicate: ({ queryKey }) => {
          const queryRequest = extractRequestFromQueryKey(queryKey);
          return !!queryRequest?.ids?.includes(request.id);
        },
        updater: (oldData) => {
          return {
            ...oldData,
            matches: updateInList({
              list: oldData?.matches ?? [],
              matchFn: (item) => item.id === request.id,
              updater: (item) => response ?? updateModelWithOptions(item, request, _options),
            }),
          };
        },
      },
    }),
    deleteOne: (request) => ({
      getSome: {
        updater: (oldData) => {
          return removeItemFromInfiniteDataList({
            infiniteData: oldData,
            matchFn: ({ id }) => id === request,
          });
        },
      },
      getOne: {
        request,
        updater: () => null,
      },
      getByIds: {
        updater: (oldData) => {
          return {
            ...oldData,
            matches: removeFromList({
              list: oldData?.matches ?? [],
              matchFn: (item) => item.id === request,
            }),
          };
        },
      },
    }),
  };

  return exampleConfig as ModelAutomaticMutationSideEffectConfigs<Model>;
};
